// Home hero: what this is, the two ways in, and live counts from the grammar engine.

import type { FC, ReactNode } from 'react'
import type { HomeData } from '@/prakrit/lib/server/home'

import Link from 'next/link'
import Image from 'next/image'
import { ArrowRight, BookOpen, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TAGLINE } from '@/prakrit/lib/content/ui-strings'
import { Bi } from '@/prakrit/components/common/bi'
import { TriLine } from '@/prakrit/components/common/tri-line'
import { CONTAINER } from '@/prakrit/components/grammar/labs/lab-ui'

const FIRST_SENTENCE = 'u2-pahla-vakya'
const BEGINNING = 'u0-kya-hai'

/** Indian digit grouping (1,23,456) without locale APIs, so server and browser render the same text. */
export const groupDigits = (n: number): string => {
    const s = String(Math.max(0, Math.round(n)))

    if (s.length <= 3) return s

    return `${s.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${s.slice(-3)}`
}

const Stat: FC<{ value: number, hi: string, en: string, accent?: boolean }> = ({ value, hi, en, accent = false }): ReactNode => (
    <div className={cn('flex flex-col gap-1 rounded-2xl border px-4 py-4', accent ? 'border-success/40 bg-success-soft' : 'border-border bg-card')}>
        <dt className='flex flex-col text-sm leading-snug'>
            <span lang='hi' className='hindi font-medium'>{hi}</span>
            <span lang='en' className='text-xs text-muted-foreground'>{en}</span>
        </dt>
        <dd className={cn('order-first font-heading text-3xl font-bold tabular-nums leading-none', accent ? 'text-success' : 'text-prakrit')}>
            {groupDigits(value)}
        </dd>
    </div>
)

const Cta: FC<{ open: boolean, href: string, primary?: boolean, icon: ReactNode, hi: string, en: string }> = ({ open, href, primary = false, icon, hi, en }): ReactNode => {
    const inner = (
        <>
            {icon}
            <span className='flex flex-col items-start leading-tight'>
                <span lang='hi' className='hindi'>{hi}</span>
                <span lang='en' className={cn('text-sm font-medium', !primary && 'text-muted-foreground')}>{en}</span>
            </span>
            {primary && <ArrowRight className='size-5' aria-hidden='true' />}
        </>
    )

    if (!open) {
        return (
            <span className='inline-flex min-h-tap items-center justify-center gap-2.5 rounded-full border border-dashed border-border px-6 py-3 font-semibold text-muted-foreground'>
                {inner}
                <span className='text-xs font-semibold'><span lang='hi' className='hindi'>जल्द</span> · <span lang='en'>Coming next</span></span>
            </span>
        )
    }

    return (
        <Link
            href={href}
            className={cn(
                'inline-flex min-h-tap items-center justify-center gap-2.5 rounded-full px-6 py-3 font-semibold shadow-card transition-colors',
                primary
                    ? 'bg-primary text-primary-foreground hover:bg-primary/85'
                    : 'border border-border bg-card hover:border-saffron/60 hover:bg-saffron/5'
            )}
        >
            {inner}
        </Link>
    )
}

export const HomeHero: FC<{ data: HomeData }> = ({ data }): ReactNode => {
    const { counts } = data

    return (
        <section id='hero' aria-labelledby='hero-heading'>
            <div className={cn(CONTAINER, 'grid items-center gap-10 pb-10 pt-8 sm:pt-12 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:gap-14 lg:pb-16 lg:pt-16')}>
                <div className='flex flex-col gap-6'>
                    <p className='inline-flex w-fit items-center gap-2.5 rounded-full border border-border bg-card/80 py-1 pl-1 pr-3.5 text-xs font-semibold shadow-sm'>
                        <Image src='/images/brand/prakrit-suvaani.png' alt='' width={56} height={56} className='size-7 rounded-full bg-white object-cover' />
                        <span lang='en'>Prakrit Suvaani</span>
                        <span aria-hidden='true' className='text-muted-foreground/60'>·</span>
                        <span lang='en' className='text-muted-foreground'>Learn Prakrit from zero</span>
                    </p>

                    <h1 id='hero-heading' className='flex flex-col'>
                        <span lang='hi' className='font-heading text-[2.75rem] font-bold leading-[1.15] text-prakrit sm:text-6xl'>प्राकृत सीखें, शून्य से</span>
                        <span lang='en' className='mt-3 font-sans text-xl font-medium leading-snug text-muted-foreground sm:text-2xl'>
                            Learn Prakrit from zero: first word, first sentence, then stories.
                        </span>
                    </h1>

                    <div className='rounded-2xl border-l-4 border-saffron bg-card/80 px-4 py-3 shadow-sm'>
                        <TriLine pra={TAGLINE.pra} hi={TAGLINE.hi} en={TAGLINE.en} size='md' unreviewed={!data.taglineReviewed} />
                    </div>

                    <p className='max-w-xl text-base leading-relaxed'>
                        <Bi
                            hi='हर प्राकृत रूप के साथ हिन्दी और अंग्रेज़ी अर्थ। हर रूप पुस्तक के नियमों से बनता है, और प्राकृत शिक्षक उसकी जाँच करते हैं।'
                            en='Hindi and English beside every Prakrit form. Every form is built from the rules of the book, and a Prakrit teacher checks it.'
                            enClassName='mt-1'
                        />
                    </p>

                    <div className='flex flex-col gap-3 sm:flex-row sm:flex-wrap'>
                        {data.firstLesson === FIRST_SENTENCE ? (
                            <Cta
                                open
                                href={`/learn/${FIRST_SENTENCE}`}
                                primary
                                icon={<Sparkles className='size-5' aria-hidden='true' />}
                                hi='5 मिनट में पहला वाक्य'
                                en='Your first sentence in 5 minutes'
                            />
                        ) : data.firstLesson ? (
                            <Cta
                                open
                                href={`/learn/${data.firstLesson}`}
                                primary
                                icon={<Sparkles className='size-5' aria-hidden='true' />}
                                hi='पहला पाठ शुरू करें'
                                en='Start your first lesson'
                            />
                        ) : (
                            // No lesson is published yet: the main button never leads to a closed page.
                            <Cta
                                open
                                href='/grammar/kal/vartaman'
                                primary
                                icon={<Sparkles className='size-5' aria-hidden='true' />}
                                hi='वर्तमान काल के रूप बनाइए'
                                en='Build your first verb forms'
                            />
                        )}
                        <Cta
                            open={data.available.includes(BEGINNING)}
                            href={`/learn/${BEGINNING}`}
                            icon={<BookOpen className='size-5 text-saffron-strong' aria-hidden='true' />}
                            hi='शुरुआत से'
                            en='Start from the alphabet'
                        />
                    </div>
                </div>

                <figure className='relative isolate'>
                    <div aria-hidden='true' className='absolute -inset-3 -z-10 rotate-2 rounded-[2rem] bg-saffron/15' />
                    <Image
                        src='/images/home/hero.jpg'
                        alt='पेड़ के नीचे गुरु एक बच्चे और एक वयस्क को ताड़पत्र से पढ़ा रहे हैं · A teacher reads from a palm-leaf manuscript to a child and an adult under a tree by a river'
                        width={1600}
                        height={1200}
                        priority
                        sizes='(min-width: 1024px) 560px, 100vw'
                        className='aspect-[4/3] h-auto w-full rounded-3xl border border-border object-cover shadow-card'
                    />
                </figure>
            </div>

            <div className={CONTAINER}>
                <h2 className='sr-only'>What is inside</h2>
                <dl className='grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5'>
                    <Stat value={counts.roots} hi='धातुएँ' en='verb roots' />
                    <Stat value={counts.nouns} hi='संज्ञा शब्द' en='nouns' />
                    <Stat value={counts.totalForms} hi='नियम से बने रूप' en='forms built by rule' />
                    {data.lessons > 0 && <Stat value={data.lessons} hi='पाठ तैयार' en='lessons ready' />}
                    <Stat value={counts.stories + counts.dialogues} hi='कथाएँ और बातचीत' en='stories and dialogues' accent />
                </dl>
            </div>
        </section>
    )
}
